import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  useMotionTemplate,
} from "framer-motion";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { IMAGES, SERVICES, MARKETS, CUSTOMERS, COMPANY } from "../data/site";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

const HUD = [
  { val: "46+", label: "Years Underground" },
  { val: "2,200 FT", label: "Longest HDD Bore" },
  { val: '36"', label: "Max Pipe Diameter" },
  { val: "CA / AZ / NV", label: "Licensed Contractor" },
];

const CHAPTERS = [
  {
    num: "01",
    title: "Subterranean Precision",
    desc: "When the work is underground, accuracy matters. Reliability matters. Experience matters. Conduit, fiber, cable, manholes — placed right, from the ground down.",
  },
  {
    num: "02",
    title: "Trenchless First",
    desc: "One of the few contractors to dive head-first into HDD over 30 years ago — drilling under rivers, highways, and mountains so the surface never knows we were there.",
  },
  {
    num: "03",
    title: "Crews From the Ground Down",
    desc: "Working foremen with years of underground experience, low turnover, and an in-house Ditch Witch boring simulator keeping operators sharp between jobs.",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.4);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const spotlight = useMotionTemplate`radial-gradient(600px circle at ${useTransform(
    sx,
    (v) => v * 100
  )}% ${useTransform(sy, (v) => v * 100)}%, rgba(37,122,219,0.18), transparent 70%)`;

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="home-hero"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={IMAGES.heroAerial}
          alt="Aerial view of an LTEC underground utility jobsite in California"
          className="h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/55 to-obsidian" />
        <div className="absolute inset-0 blueprint-grid" />
      </motion.div>
      <motion.div style={{ background: spotlight }} className="pointer-events-none absolute inset-0" />

      <span className="pointer-events-none absolute left-4 top-24 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 sm:left-8">
        35.6481&deg; N
      </span>
      <span className="pointer-events-none absolute right-4 top-24 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 sm:right-8">
        120.6637&deg; W
      </span>

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-10 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-6 inline-flex items-center gap-2 border border-blueprint/30 bg-obsidian/60 px-3 py-1.5 backdrop-blur"
        >
          <span className="h-1.5 w-1.5 rotate-45 bg-blueprint" />
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-blueprint">
            Leo Tidwell Excavating Corp &middot; CA / AZ / NV Licensed
          </span>
        </motion.div>

        <h1 className="font-display text-[17vw] sm:text-[13vw] lg:text-[9.5rem] font-black uppercase leading-[0.88] tracking-tight text-white">
          <MaskedLine delay={0.25}>Built</MaskedLine>
          <MaskedLine delay={0.37}>
            <span className="text-brand-light">Beneath</span>
          </MaskedLine>
          <MaskedLine delay={0.49}>California.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.75 }}
          className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
        >
          Underground utility construction built on experience, precision, and crews who
          know the work from the ground down — for {COMPANY.years} years.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          <Link
            to="/capabilities"
            data-testid="hero-cta-capabilities"
            className="group inline-flex items-center gap-2 bg-brand px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-light"
          >
            Explore Capabilities
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/request-bid"
            data-testid="hero-cta-bid"
            className="inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:border-blueprint hover:text-blueprint"
          >
            Request Project Estimate
          </Link>
        </motion.div>

        <motion.dl
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-14 grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-4"
          data-testid="hero-metrics-hud"
        >
          {HUD.map((m) => (
            <div key={m.label} className="bg-obsidian/80 px-4 py-4 backdrop-blur sm:px-6">
              <dt className="order-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {m.label}
              </dt>
              <dd className="font-display text-2xl font-bold text-white sm:text-3xl">{m.val}</dd>
            </div>
          ))}
        </motion.dl>
      </motion.div>
    </section>
  );
};

const Manifesto = () => (
  <section data-testid="home-manifesto" className="py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>The LTEC Standard</Eyebrow>
      </Reveal>
      <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.num} delay={i * 0.08}>
            <div className="group grid gap-4 py-10 sm:grid-cols-[120px_1fr_1.4fr] sm:items-baseline sm:gap-8 sm:py-14">
              <span className="font-mono text-sm text-brand-light">/{c.num}</span>
              <h3 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white transition-colors group-hover:text-brand-light">
                {c.title}
              </h3>
              <p className="text-sm sm:text-base leading-relaxed text-slate-400">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CapabilitiesGrid = () => (
  <section data-testid="home-capabilities" className="py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <Reveal>
          <Eyebrow>What We Build</Eyebrow>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
            Core Capabilities
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/capabilities"
            data-testid="home-capabilities-view-all"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-blueprint hover:text-white"
          >
            Full capability matrix
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.slice(0, 6).map((s, i) => (
          <Reveal key={s.id} delay={(i % 3) * 0.08} className="h-full">
            <Link
              to={`/capabilities#${s.id}`}
              data-testid={`capability-card-${s.id}`}
              className="group relative flex h-full flex-col bg-granite transition-colors hover:bg-[#141d2e]"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-granite via-transparent to-transparent" />
                <span className="absolute left-4 top-4 border border-white/20 bg-obsidian/70 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 backdrop-blur">
                  {s.tag}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-display text-2xl font-bold uppercase leading-tight text-white">
                    {s.title}
                  </h3>
                  <span className="font-mono text-xs text-slate-500">/{s.num}</span>
                </div>
                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-400">{s.blurb}</p>
                <span className="mt-auto inline-flex items-center gap-1.5 pt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-light">
                  View section
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const LegacyStats = () => {
  const stats = [
    { val: "46+", label: "Years as a utilities contractor" },
    { val: "45+", label: "Ditch Witch drilling units owned & operated" },
    { val: "$12M", label: "Largest project completed" },
    { val: "7", label: "Fiber-blowing machines in fleet" },
  ];
  return (
    <section data-testid="home-legacy" className="border-y border-white/10 bg-granite/50 py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Field-Proven Since Day One</Eyebrow>
          <h2 className="mt-4 max-w-3xl font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
            Experience you can verify, not just claim
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-px border border-white/10 bg-white/10 lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <div className="h-full bg-obsidian p-6 sm:p-8">
                <p className="font-display text-5xl font-black text-brand-light sm:text-6xl">{s.val}</p>
                <p className="mt-3 text-sm leading-snug text-slate-400">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
              Markets &middot; {MARKETS.join(" / ")}
            </span>
          </div>
          <p className="mt-4 max-w-4xl text-sm leading-relaxed text-slate-400" data-testid="home-customers">
            Trusted by major carriers and utilities including {CUSTOMERS.join(", ")}.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const SafetyBanner = () => (
  <section data-testid="home-safety-banner" className="py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="relative overflow-hidden border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-granite to-obsidian p-8 sm:p-14">
          <div className="absolute inset-0 blueprint-grid opacity-60" />
          <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <p className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
                <ShieldCheck className="h-4 w-4" /> Safety Discipline
              </p>
              <h2 className="mt-4 font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
                Every utility located before a bucket touches ground
              </h2>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-300">
                Positive location is the law — and our standard. Hydro-vac potholing, trained
                operators, and fully insured crews on every jobsite.
              </p>
            </div>
            <Link
              to="/safety"
              data-testid="home-safety-cta"
              className="group inline-flex shrink-0 items-center gap-2 bg-amber-500 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-amber-400"
            >
              Our Safety Protocols
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const FinalCTA = () => (
  <section data-testid="home-final-cta" className="relative overflow-hidden border-t border-white/10 py-24 sm:py-36">
    <img
      src={IMAGES.trencher}
      alt=""
      className="absolute inset-0 h-full w-full object-cover opacity-15"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/80 to-obsidian" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <Reveal>
        <Eyebrow className="justify-center">Large Bids &amp; Day-to-Day T&amp;M</Eyebrow>
        <h2 className="mx-auto mt-6 max-w-4xl font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tight text-white">
          Ready to build <span className="text-brand-light">beneath</span> California?
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-400">
          Proposals on large projects and day-to-day time-and-material work. Send the
          scope — our Paso Robles office handles all bidding directly.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/request-bid"
            data-testid="final-cta-contact"
            className="inline-flex items-center gap-2 bg-brand px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-light"
          >
            Request a Bid <ArrowUpRight className="h-4 w-4" />
          </Link>
          <a
            href={COMPANY.phoneHref}
            data-testid="final-cta-call"
            className="inline-flex items-center gap-2 border border-white/25 px-8 py-4 text-sm font-semibold text-white transition-colors hover:border-blueprint hover:text-blueprint"
          >
            Call {COMPANY.phone}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <Manifesto />
      <CapabilitiesGrid />
      <LegacyStats />
      <SafetyBanner />
      <FinalCTA />
    </>
  );
}
